import type { ActionNodeData } from '../nodes/ActionNode';
import type { ValidationError } from './workflowValidator';
import { safeCastString, safeCastNumber, safeCastArray, isPlainRecord } from './typeGuards';

export interface ActionStrategy {
    hydrate: (config: Record<string, unknown>) => Partial<ActionNodeData>;
    serialize: (data: ActionNodeData) => Record<string, unknown>;
    validate: (data: ActionNodeData, stepId: string, title: string) => ValidationError[];
}

export const ACTION_STRATEGIES: Record<string, ActionStrategy> = {
    'Action.Message.AutomatedResponse': {
        hydrate: (config) => {
            const mediaFileList = safeCastArray(config.mediaFileList);
            return {
                message: safeCastString(config.messageBody ?? config.message),
                templateId: safeCastString(config.templateId),
                templateName: safeCastString(config.templateName ?? config.friendlyName),
                templateIntegId: safeCastString(config.templateIntegId),
                mediaFileList,
                hasMedia: mediaFileList.length > 0,
                components: config.components,
                fieldNameAndValueMap: isPlainRecord(config.fieldNameAndValueMap)
                    ? config.fieldNameAndValueMap
                    : undefined,
            };
        },
        serialize: (data) => ({
            from: '',
            pageId: '',
            to: '',
            messageBody: data.message ?? '',
            mediaFileList: data.mediaFileList ?? [],
            userId: '',
            associatedObjectId: '',
            associatedObjectType: '',
            templateId: data.templateId ?? '',
            templateName: data.templateName ?? '',
            templateIntegId: data.templateIntegId ?? '',
            components: data.components,
            fieldNameAndValueMap: data.fieldNameAndValueMap,
        }),
        validate: (data, stepId, title) => {
            if (!data.templateId && !data.message?.trim() && !data.hasMedia) {
                return [{ stepId, stepTitle: title, message: 'Enter a response message or select a template.' }];
            }
            return [];
        },
    },

    'Action.Notify.NotifyUser': {
        hydrate: (config) => ({
            recordOwner: config.includeRecordOwner === true,
            accountOwner: config.includeAccountOwner === true,
            includeAdditionalEmails: safeCastArray(config.customEmails).length > 0,
            additionalEmails: safeCastArray(config.customEmails),
        }),
        serialize: (data) => ({
            userIds: [],
            includeAccountOwner: data.accountOwner ?? false,
            includeRecordOwner: data.recordOwner ?? false,
            customEmails: data.includeAdditionalEmails ? data.additionalEmails ?? [] : [],
        }),
        validate: (data, stepId, title) => {
            const hasRecipient =
                data.recordOwner ||
                data.accountOwner ||
                (data.includeAdditionalEmails && (data.additionalEmails?.length ?? 0) > 0);
            if (!hasRecipient) {
                return [
                    {
                        stepId,
                        stepTitle: title,
                        message:
                            'Select at least one email recipient (Record Owner, Account Owner, or Additional Email Addresses).',
                    },
                ];
            }
            if (
                data.includeAdditionalEmails &&
                (!data.additionalEmails || data.additionalEmails.length === 0)
            ) {
                return [
                    {
                        stepId,
                        stepTitle: title,
                        message: 'Add at least one email address when Additional Email Addresses is selected.',
                    },
                ];
            }
            return [];
        },
    },

    'Action.Crm.AddNote': {
        hydrate: (config) => ({
            logRecordsFor: safeCastString(config.note) ?? 'all',
            logRecordAsThread: config.shouldThreadActivities === true ? 'true' : 'false',
        }),
        serialize: (data) => ({
            note: data.message,
            shouldThreadActivities: data.logRecordAsThread === 'true',
        }),
        validate: () => [],
    },

    'Action.Crm.LogActivity': {
        hydrate: (config) => ({
            logRecordsFor: safeCastString(config.note) ?? 'all',
            logRecordAsThread: config.shouldThreadActivities === true ? 'true' : 'false',
        }),
        serialize: (data) => ({
            logRecordsFor: data.logRecordsFor || 'all',
            shouldThreadActivities: data.logRecordAsThread === 'true',
        }),
        validate: () => [],
    },

    'Action.Crm.CreateLead': {
        hydrate: (config) => ({
            createLead: config.enabled === false ? 'false' : 'true',
        }),
        serialize: (data) => ({
            enabled: data.createLead !== 'false',
        }),
        validate: () => [],
    },

    'Action.FlowControl.Delay': {
        hydrate: (config) => {
            const amount = safeCastNumber(config.delayAmount) ?? 0;
            const unit = safeCastString(config.delayUnit)?.toLowerCase() ?? 'minutes';
            const totalMinutes =
                unit === 'days' ? amount * 24 * 60 : unit === 'hours' ? amount * 60 : amount;
            return {
                delayDays: Math.floor(totalMinutes / (24 * 60)),
                delayHours: Math.floor((totalMinutes % (24 * 60)) / 60),
                delayMinutes: Math.floor(totalMinutes % 60),
            };
        },
        serialize: (data) => {
            const days = data.delayDays ?? 0;
            const hours = data.delayHours ?? 0;
            const minutes = data.delayMinutes ?? 0;
            const totalMinutes = days * 24 * 60 + hours * 60 + minutes;
            return { delayAmount: totalMinutes, delayUnit: 'minutes' };
        },
        validate: (data, stepId, title) => {
            const days = data.delayDays ?? 0;
            const hours = data.delayHours ?? 0;
            const minutes = data.delayMinutes ?? 0;
            const totalMinutes = days * 24 * 60 + hours * 60 + minutes;
            const MIN_DELAY_MINUTES = 15;
            const MAX_DELAY_MINUTES = 30 * 24 * 60;
            if (totalMinutes < MIN_DELAY_MINUTES) {
                return [{ stepId, stepTitle: title, message: 'Delay must be at least 15 minutes.' }];
            }
            if (totalMinutes > MAX_DELAY_MINUTES) {
                return [{ stepId, stepTitle: title, message: 'Delay cannot exceed 30 days.' }];
            }
            return [];
        },
    },
};

export function getActionStrategy(actionCode: string | undefined): ActionStrategy | undefined {
    return actionCode ? ACTION_STRATEGIES[actionCode] : undefined;
}
